import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Button, MenuItem, Alert, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TablePagination, Chip } from '@mui/material';

const MyRequestsSection = ({ activeMenu, shifts, assignForm, setAssignForm, woDate, setWoDate, handleRequestChange, handleWoRequest, myShiftReqs, myWoReqs }) => {
  const [pageShift, setPageShift] = useState(0);
  const [rowsPerPageShift, setRowsPerPageShift] = useState(5);
  const [pageWo, setPageWo] = useState(0);
  const [rowsPerPageWo, setRowsPerPageWo] = useState(5);

  // Common button styling
  const submitButtonSx = {
    mt: 1,
    alignSelf: 'flex-start',
    width: { xs: '100%', sm: 'auto' },
    maxWidth: { sm: '200px' },
    py: 1,
    px: 3,
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        {activeMenu === 'shift_req' && 'Request Shift Change'}
        {activeMenu === 'wo_req' && 'Request Week Off'}
        {activeMenu === 'req_history' && 'My Shift & WO Requests'}
      </Typography>

      {activeMenu === 'shift_req' && (
        <Paper sx={{ p: 4, maxWidth: 600 }}>
          <Box component="form" onSubmit={handleRequestChange} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField 
                type="date" 
                label="Start Date" 
                required 
                InputLabelProps={{ shrink: true }} 
                value={assignForm.start_date} 
                onChange={e => setAssignForm({ ...assignForm, start_date: e.target.value })} 
                fullWidth 
              />
              <TextField 
                type="date" 
                label="End Date" 
                required 
                InputLabelProps={{ shrink: true }} 
                value={assignForm.end_date} 
                onChange={e => setAssignForm({ ...assignForm, end_date: e.target.value })} 
                fullWidth 
              />
            </Box>
            <TextField 
              select 
              label="Requested Shift" 
              required 
              value={assignForm.requested_shift_id} 
              onChange={e => setAssignForm({ ...assignForm, requested_shift_id: e.target.value })}
            >
              {shifts.map(s => <MenuItem key={s.id} value={s.id}>{s.shift_name}</MenuItem>)}
            </TextField>
            <TextField 
              label="Reason for Change" 
              required 
              multiline 
              rows={2} 
              value={assignForm.reason} 
              onChange={e => setAssignForm({ ...assignForm, reason: e.target.value })} 
            />
            <Button 
              type="submit" 
              variant="contained" 
              size="medium"
              sx={submitButtonSx}
            >
              Submit Request
            </Button>
          </Box>
        </Paper>
      )}

      {activeMenu === 'wo_req' && (
        <Paper sx={{ p: 4, maxWidth: 600 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Your Week Off balance depends on your HR Level and the number of Sundays in the current cycle. Maximum 2 consecutive WOs allowed.
          </Alert>
          <Box component="form" onSubmit={handleWoRequest} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField 
              type="date" 
              label="Target Date" 
              required 
              InputLabelProps={{ shrink: true }} 
              value={woDate} 
              onChange={e => setWoDate(e.target.value)} 
            />
            <Button 
              type="submit" 
              variant="contained" 
              size="medium"
              sx={submitButtonSx}
            >
              Submit Week Off Request
            </Button>
          </Box>
        </Paper>
      )}

      {/* Rest of the component remains the same */}
      {activeMenu === 'req_history' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Shift Requests */}
          <Paper sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: '#fafafa', borderBottom: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle1">Shift Change Requests</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Requested Dates</TableCell>
                    <TableCell>Target Shift</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {myShiftReqs.length === 0 ? <TableRow><TableCell colSpan={4} align="center">No requests found.</TableCell></TableRow> : 
                    myShiftReqs.slice(pageShift * rowsPerPageShift, pageShift * rowsPerPageShift + rowsPerPageShift).map(req => (
                    <TableRow key={req.id} hover>
                      <TableCell>{req.start_date} to {req.end_date}</TableCell>
                      <TableCell>{shifts.find(s => s.id === req.requested_shift_id)?.shift_name}</TableCell>
                      <TableCell>{req.reason}</TableCell>
                      <TableCell><Chip label={req.status} color={req.status === 'APPROVED' ? 'success' : (req.status === 'PENDING' ? 'warning' : 'error')} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination 
              component="div" 
              count={myShiftReqs.length} 
              page={pageShift} 
              onPageChange={(e, p) => setPageShift(p)} 
              rowsPerPage={rowsPerPageShift} 
              onRowsPerPageChange={(e) => { setRowsPerPageShift(parseInt(e.target.value, 10)); setPageShift(0); }} 
            />
          </Paper>

          {/* WO Requests */}
          <Paper sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: '#fafafa', borderBottom: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle1">Week Off Requests</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Requested Date</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {myWoReqs.length === 0 ? <TableRow><TableCell colSpan={2} align="center">No requests found.</TableCell></TableRow> : 
                    myWoReqs.slice(pageWo * rowsPerPageWo, pageWo * rowsPerPageWo + rowsPerPageWo).map(req => (
                    <TableRow key={req.id} hover>
                      <TableCell>{req.date}</TableCell>
                      <TableCell><Chip label={req.status} color={req.status === 'APPROVED' ? 'success' : (req.status === 'PENDING' ? 'warning' : 'error')} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination 
              component="div" 
              count={myWoReqs.length} 
              page={pageWo} 
              onPageChange={(e, p) => setPageWo(p)} 
              rowsPerPage={rowsPerPageWo} 
              onRowsPerPageChange={(e) => { setRowsPerPageWo(parseInt(e.target.value, 10)); setPageWo(0); }} 
            />
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default MyRequestsSection;